// Android hardware back button (APK only). Without this, Android back exits the
// whole app. We drive in-app navigation generally:
//   • deeper than the top-level Inbox list → go back within the app's router
//     history (navigate(-1)) — works for chat, Analytics, Settings, any sub-route
//   • on the top-level Inbox / ticket list → minimize the app, never exit
//
// Native-only (guarded by Capacitor.isNativePlatform); on web/desktop this is a
// no-op and normal browser back is untouched. The @capacitor/app plugin is
// imported lazily so the web bundle never touches the native bridge. Registered
// once; the listener is removed on unmount (no duplicate handlers).

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation, type Location } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { runBackInterceptors } from '../lib/backInterceptors';
import { INBOX_PATH } from '../lib/routeConfig';

/** True when the given location is the top-level Inbox ticket list (no open
 *  ticket, no open conversation) — the screen where back should minimize. */
export function isTopLevel(loc: Pick<Location, 'pathname' | 'search'>): boolean {
  return loc.pathname === INBOX_PATH && !new URLSearchParams(loc.search).get('conversation');
}

export function isCustomAndroidShell(
  userAgent = navigator.userAgent,
  bridgeMarker = window.__CHATBOT_ANDROID__ === true,
): boolean {
  return bridgeMarker || userAgent.includes('ChatbotAndroid/');
}

declare global {
  interface Window {
    __CHATBOT_ANDROID__?: boolean;
  }
}

export function useAndroidBack(): void {
  const navigate = useNavigate();
  const location = useLocation();
  // Keep the latest location in a ref so the once-registered listener never
  // reads a stale closure.
  const locRef = useRef(location);
  locRef.current = location;

  useEffect(() => {
    // The production APK is a hardened native WebView shell rather than a
    // Capacitor Activity. It dispatches this cancelable event and performs the
    // native fallback only when React did not consume it.
    // The custom user-agent exists before the first application script runs;
    // the bridge marker is installed after page load as a second signal.
    if (isCustomAndroidShell()) {
      const handleNativeBack = (event: Event) => {
        if (runBackInterceptors()) {
          event.preventDefault();
          return;
        }
        if (!isTopLevel(locRef.current)) {
          event.preventDefault();
          const historyIndex = Number(window.history.state?.idx ?? 0);
          if (historyIndex > 0) navigate(-1);
          else navigate(INBOX_PATH, { replace: true });
        }
      };
      window.addEventListener('chatbot:native-back', handleNativeBack);
      return () => window.removeEventListener('chatbot:native-back', handleNativeBack);
    }

    if (!Capacitor.isNativePlatform()) return; // web/desktop: normal browser back
    let active = true;
    let remove = () => {};
    void (async () => {
      const { App } = await import('@capacitor/app');
      const handle = await App.addListener('backButton', () => {
        // Let transient UI (an open menu/sheet) consume back first.
        if (runBackInterceptors()) return;
        if (isTopLevel(locRef.current)) {
          void App.minimizeApp();
        } else {
          navigate(-1);
        }
      });
      if (!active) { void handle.remove(); return; } // unmounted before registration finished
      remove = () => { void handle.remove(); };
    })();
    return () => { active = false; remove(); };
  }, [navigate]); // navigate is stable → registers exactly once
}
